<template>
  <div class="uv" :class="{ bare }" :style="{ '--tc': side.bright }">
    <div v-if="!bare" class="uv-head">
      <UnitSprite :unit="unit" :team-color="side.pal" :fallback-src="fallback" :animated="true" :size="64" />
      <div class="uv-id">
        <div class="uv-name">{{ unit.Name }} <span class="uv-grade" :style="{ background: grade.color }">{{ grade.grade }}</span></div>
        <div class="uv-sub">{{ unit.Gender === 'Monster' ? '' : unit.Gender + ' ' }}{{ unit.Class }} · {{ unit.Sign }} · {{ side.label }}</div>
        <div class="uv-stats"><b>HP</b> {{ stats.hp }} <b>MP</b> {{ stats.mp }} <b>Spd</b> {{ stats.speed }} <b>PA</b> {{ stats.pa }} <b>MA</b> {{ stats.ma }} <b>Br</b> {{ unit.Brave }} <b>Fa</b> {{ unit.Faith }}</div>
        <KitLine class="uv-kit" :unit="unit" />
      </div>
      <EvadeGrid :ev="info.evade" :sources="info.sources" size="lg" />
      <div class="uv-why" v-if="drivers.up.length || drivers.down.length">
        <div class="uv-why-title">Grade drivers</div>
        <span v-for="p in drivers.up" :key="'u' + p.label" class="uv-chip up">{{ p.label }} +{{ p.contribution }}</span>
        <span v-for="p in drivers.down" :key="'d' + p.label" class="uv-chip down">{{ p.label }} {{ p.contribution }}</span>
      </div>
      <div class="uv-btns"><button class="uv-full" @click="$emit('full')">Full card</button><button class="uv-close" @click="$emit('close')" aria-label="Close">✕</button></div>
    </div>

    <div class="uv-pretend">
      <span>Pretend each target has</span>
      <button v-for="[k, label] in PRETEND" :key="k" class="uv-pt" :class="{ on: pretend[k] }" :aria-pressed="String(!!pretend[k])" @click="pretend = { ...pretend, [k]: !pretend[k] }">{{ label }}</button>
    </div>
    <table class="uv-grid" v-if="rows.length">
      <thead>
        <tr><th>Against {{ foe.label }}</th><th v-for="(e, k) in foe.units" :key="k" :style="{ color: foe.bright }">{{ e.Name }}<i>{{ e.Class }} · {{ foeHp[k] }} HP</i></th></tr>
      </thead>
      <tbody>
        <tr v-for="r in rows" :key="r.name">
          <td class="uv-ab">{{ r.name }}<i v-if="r.note">{{ r.note }}</i></td>
          <td v-for="(c, k) in r.cells" :key="k" class="uv-cell" :class="[c.cls, { open: isOpen(r.name, k) }]" v-tip="c.text ? c.title + ' · click for the breakdown' : ''"
              :tabindex="c.text ? 0 : null" @click="c.text && toggle(r.name, k)" @keydown.enter="c.text && toggle(r.name, k)">
            <template v-if="c.text">{{ c.text }}<small v-if="c.hit">{{ c.hit }}</small></template>
            <span v-else class="uv-none">–</span>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-if="opened" class="uv-drill">
      <div class="uv-drill-head">{{ opened.ability }} → {{ opened.target }} <button class="uv-drill-x" @click="open = null" aria-label="Close the breakdown">✕</button></div>
      <div class="uv-drill-sim">The sim: <b>{{ opened.sim }}</b></div>
      <template v-if="opened.cell">
        <div class="uv-drill-sub">The card's formula{{ opened.cell.kind === 'percent' ? ', chance to land' : '' }}, step by step</div>
        <div class="uv-steps">
          <span v-for="(st, i) in opened.cell.steps" :key="i" class="uv-step"><i>{{ st.label }}</i><small v-if="st.factor">{{ st.factor }}</small><b>{{ st.value }}</b></span>
          <span v-if="!opened.cell.steps.length" class="uv-step"><i>No modifiers, base {{ opened.cell.kind === 'percent' ? 'chance' : 'damage' }}</i><b>{{ opened.cell.display }}</b></span>
        </div>
        <div v-if="opened.cell.kind === 'damage'" class="uv-drill-hit">
          <template v-if="opened.cell.hitDirs && opened.cell.hitDirs.magical">Magic, so it lands {{ opened.cell.hitDirs.front }}% from any direction.</template>
          <template v-else-if="opened.cell.hitDirs">Lands {{ opened.cell.hitDirs.front }}% from the front · {{ opened.cell.hitDirs.side }}% side · {{ opened.cell.hitDirs.back }}% back.</template>
          <template v-else>Can't be dodged.</template>
        </div>
      </template>
      <div v-else class="uv-drill-sub">No formula breakdown for this one: the sim models it directly.</div>
    </div>
    <p class="uv-foot">The sim's estimate for each of {{ unit.Name }}'s options against each foe, landing from the front: damage, then the chance it lands. <b class="ko">KO</b> = enough to finish them from full HP; <b class="heal">heals</b> = they absorb it. Supports and reactions aren't listed. Click a number for the formula, step by step.</p>
  </div>
</template>

<script>
// The fight spotlight's unit panel (FightSpotlight): a unit's card (grade, stats, kit, what drives the grade)
// and, like the old Fight View's matrix, each of its options against each foe. The numbers come from the battle sim
// itself (simPrep's actions, battleSim.evalDetail): damage after zodiac, elements, Faith and the target's gear, and the
// chance it lands from the front.
import UnitSprite from '../UnitSprite.vue';
import KitLine from './KitLine.vue';
import EvadeGrid from './EvadeGrid.vue';
import { unitInfo } from './unitInfo.js';
import { prepTeam } from '../../lib/simPrep.js';
import { evalDetail, basicAttack } from '../../lib/battleSim.js';
import { unitGrade, topParts } from '../../lib/unitGrade.js';
import { unitCombatStats } from '../../lib/unitStats.js';
import { rosterStats } from '../../lib/preload.js';
import { fightMatrix, matrixRowFor, PRETEND_STATUSES } from '../../lib/fightMatrix.js';

// a pretend status → how the sim marks it on a unit
const SIM_STATUS = { protect: 'Protect', shell: 'Shell', sleeping: 'Sleep', frogChicken: 'Frog', oil: 'Oil' };

export default {
  name: 'UnitVsPanel',
  components: { UnitSprite, KitLine, EvadeGrid },
  props: {
    unit: { type: Object, required: true },
    side: { type: Object, required: true },   // the unit's side of the match (label, bright, pal, units)
    foe: { type: Object, required: true },    // the other side
    gd: { type: Object, required: true },     // game data (charStats, itemschanged, notAbles, abilitiesInfo, monsterSkills)
    fallback: { type: String, default: '' },
    bare: { type: Boolean, default: false }, // just the matrix (inside the new full card)
  },
  emits: ['close', 'full'],
  data() { return { PRETEND: PRETEND_STATUSES, pretend: {}, open: null }; },
  watch: { unit() { this.open = null; }, foe() { this.open = null; } },
  computed: {
    info() { return unitInfo(this.unit, this.gd); },
    stats() { try { return unitCombatStats(this.unit, this.gd.charStats, this.gd.itemschanged, this.gd.notAbles).stats; } catch (e) { return {}; } },
    grade() { return unitGrade(this.unit, rosterStats, this.gd); },
    drivers() { return topParts(this.grade.parts || [], 2); },
    specs() {
      // place the two teams facing each other; each option is aimed at its foe from the panel in front of it. maxHp is
      // what a battle's start sets; without it the %-of-max-HP moves (Demi, …) came out NaN.
      const A = prepTeam(this.side.units, this.gd), B = prepTeam(this.foe.units, this.gd);
      A.forEach((s, i) => Object.assign(s, { maxHp: s.hp, maxMp: s.mp, team: 'A', x: 1 + 2 * i, y: 3, z: 0, alive: true, ct: 50, status: s.status || {}, facing: { x: 0, y: 1 } }));
      B.forEach((s, i) => Object.assign(s, { maxHp: s.hp, maxMp: s.mp, team: 'B', x: 1 + 2 * i, y: 5, z: 0, alive: true, ct: 50, status: s.status || {}, facing: { x: 0, y: -1 } }));
      // the pretend statuses, put on every foe the way the sim marks them (Charging: a spell on its way)
      for (const b of B) {
        for (const [k, st] of Object.entries(SIM_STATUS)) if (this.pretend[k]) b.status = { ...b.status, [st]: 99 };
        if (this.pretend.charging) b.charging = { action: { name: 'a spell', kind: 'buff', aoe: 0 }, target: b, ctr: 3, mpDue: 0 };
      }
      return { A, B };
    },
    foeHp() { return this.specs.B.map((s) => s.hp); },
    // the card formulas' table (lib/fightMatrix), for the step-by-step breakdown of a clicked number
    matrix() { try { return fightMatrix(this.unit, this.side.units, this.foe.units, this.gd, this.pretend); } catch (e) { return null; } },
    opened() {
      if (!this.open) return null;
      const r = this.rows.find((x) => x.name === this.open.name), c = r && r.cells[this.open.k];
      if (!c || !c.text) return null;
      const mr = matrixRowFor(this.matrix, r.name);
      return { ability: r.name, target: this.foe.units[this.open.k].Name, sim: c.title, cell: mr ? mr.cells[this.open.k] : null };
    },
    rows() {
      const { A, B } = this.specs, idx = this.side.units.indexOf(this.unit), me = A[idx];
      if (!me) return [];
      const units = [...A, ...B], seen = new Set(), out = [];
      const acts = [basicAttack(me), ...(me.actions || [])].filter((a) => a && !a.mathCast && !a.homing && (a.kind === 'damage' || a.kind === 'status' || a.kind === 'mpDamage'));
      for (const a of acts) {
        if (seen.has(a.name)) continue; seen.add(a.name);
        const cells = B.map((e) => {
          const ox = me.x, oy = me.y; me.x = e.x; me.y = e.y - 1; // stand in front of this foe
          let r = null;
          try { r = (evalDetail(me, a, e, units, me, e) || []).find((x) => x.t === e && !x.mpCost); } catch (err) { r = null; } finally { me.x = ox; me.y = oy; }
          if (!r) return {};
          const hit = r.p != null ? Math.round(100 * Math.min(1, r.p)) + '%' : '';
          if (a.kind === 'status') return { text: (a.status || (a.statuses || [])[0] || 'Status') + ' ', hit, cls: 'st', title: `${a.name} → ${e.name}: ${hit} to land` };
          if (r.dmg == null) return {};
          if (r.dmg < 0) return { text: 'heals ' + -Math.round(r.dmg) + ' ', hit, cls: 'heal', title: `${a.name} → ${e.name}: absorbed, heals ${-Math.round(r.dmg)}, ${hit} to land` };
          const ko = r.dmg >= e.hp;
          return { text: Math.round(r.dmg) + (ko ? ' KO ' : ' '), hit, cls: ko ? 'ko' : r.dmg >= e.hp / 2 ? 'big' : '', title: `${a.name} → ${e.name}: ${Math.round(r.dmg)} of ${e.hp} HP, ${hit} to land` };
        });
        if (cells.some((c) => c.text)) out.push({ name: a.name, note: a.charge ? `charges ${a.charge}` : a.mp ? `${a.mp} MP` : '', cells });
      }
      return out;
    },
  },
  methods: {
    isOpen(name, k) { return !!this.open && this.open.name === name && this.open.k === k; },
    toggle(name, k) { this.open = this.isOpen(name, k) ? null : { name, k }; },
  },
};
</script>

<style scoped>
.uv.bare { border-top: 1px solid rgba(217,180,94,.3); }
.uv { margin-top: 1px; padding: 12px 14px 10px; background: linear-gradient(180deg, #1b2131, #0e121c); border-top: 2px solid var(--tc); }
.uv-head { position: relative; display: flex; gap: 14px; align-items: flex-start; flex-wrap: wrap; }
.uv-id { flex: 1; min-width: 240px; }
.uv-name { font-family: var(--cond); font-weight: 700; font-size: 22px; color: #f3ecd8; }
.uv-grade { display: inline-block; margin-left: 6px; padding: 0 8px; border-radius: 3px; font-size: 15px; color: #fff; text-shadow: 0 1px 0 rgba(0,0,0,.5); vertical-align: 3px; }
.uv-sub { font-size: 13px; color: var(--mute); }
.uv-stats { margin-top: 3px; font-size: 13px; color: var(--ink); }
.uv-stats b { font-family: var(--cond); font-weight: 600; color: var(--brass); margin-left: 8px; }
.uv-stats b:first-child { margin-left: 0; }
.uv-kit { margin-top: 2px; font-size: 12px; color: var(--mute); }
.uv-why { max-width: 360px; }
.uv-why-title { font-family: var(--cond); font-weight: 700; font-size: 11px; letter-spacing: 1.6px; text-transform: uppercase; color: var(--brass); margin-bottom: 3px; }
.uv-chip { display: inline-block; margin: 0 4px 4px 0; padding: 1px 7px; border-radius: 10px; font-size: 12px; }
.uv-chip.up { background: rgba(80,170,90,.2); color: #9fe0a6; }
.uv-chip.down { background: rgba(200,80,70,.2); color: #f0a49b; }
.uv-btns { position: absolute; top: 0; right: 0; display: flex; gap: 6px; }
.uv-close, .uv-full { background: transparent; border: 1px solid #3a4560; color: var(--ink); border-radius: 3px; cursor: pointer; padding: 1px 7px; font: inherit; font-size: 13px; }
.uv-full { font-family: var(--cond); font-weight: 600; letter-spacing: .5px; text-transform: uppercase; color: var(--brass); border-color: #7a5f2e; }
.uv-full:hover { background: rgba(217,180,94,.12); }
.uv-grid { width: 100%; margin-top: 10px; border-collapse: collapse; font-size: 13px; }
.uv-grid th { text-align: left; font-family: var(--cond); font-weight: 700; font-size: 14px; padding: 4px 6px; border-bottom: 1px solid rgba(217,180,94,.35); }
.uv-grid th:first-child { color: var(--brass); letter-spacing: 1px; text-transform: uppercase; font-size: 12px; }
.uv-grid th i { display: block; font-style: normal; font-weight: 500; font-size: 11.5px; color: var(--mute); }
.uv-grid td { padding: 4px 6px; border-bottom: 1px solid rgba(255,255,255,.05); }
.uv-ab { font-family: var(--cond); font-weight: 600; font-size: 14.5px; color: #f3ecd8; white-space: nowrap; }
.uv-ab i { font-style: normal; font-weight: 500; font-size: 11.5px; color: var(--mute); margin-left: 6px; }
.uv-cell { font-variant-numeric: tabular-nums; color: var(--ink); }
.uv-cell small { color: var(--mute); }
.uv-cell.big { color: #f3d68e; }
.uv-cell.ko { color: #ff8a7a; font-weight: 700; }
.uv-cell.heal { color: #7fd3ff; }
.uv-cell.st { color: #c9b6ff; }
.uv-none { color: #4a5266; }
.uv-cell[tabindex] { cursor: pointer; }
.uv-cell[tabindex]:hover, .uv-cell.open { background: rgba(217,180,94,.1); outline: none; }
.uv-cell.open { box-shadow: inset 0 0 0 1px var(--brass); }
.uv-pretend { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 6px; margin-top: 8px; font-size: 12px; color: var(--mute); }
.uv-pt { padding: 1px 8px; border: 1px solid #3a4560; border-radius: 10px; background: transparent; color: var(--ink); font: inherit; font-size: 12px; cursor: pointer; }
.uv-pt.on { border-color: var(--brass); background: rgba(217,180,94,.16); color: var(--brass-hi); }
.uv-drill { margin-top: 8px; padding: 8px 10px; border: 1px solid rgba(217,180,94,.35); border-radius: 4px; background: rgba(0,0,0,.2); font-size: 13px; }
.uv-drill-head { display: flex; align-items: center; font-family: var(--cond); font-weight: 700; font-size: 16px; color: var(--ink-hi, #f3ecd8); }
.uv-drill-x { margin-left: auto; background: none; border: 0; color: var(--mute); cursor: pointer; font-size: 14px; }
.uv-drill-sim { margin: 2px 0 6px; color: var(--ink); } .uv-drill-sim b { font-weight: 600; color: #f3ecd8; }
.uv-drill-sub { font-family: var(--cond); font-weight: 700; font-size: 11.5px; letter-spacing: 1.2px; text-transform: uppercase; color: var(--brass); margin-bottom: 4px; }
.uv-steps { display: flex; flex-wrap: wrap; gap: 4px; }
.uv-step { display: inline-flex; align-items: baseline; gap: 5px; padding: 2px 8px; border: 1px solid #3a4560; border-radius: 3px; }
.uv-step i { font-style: normal; color: var(--mute); font-size: 12px; } .uv-step small { color: var(--brass); } .uv-step b { color: #f3ecd8; font-variant-numeric: tabular-nums; }
.uv-drill-hit { margin-top: 6px; color: var(--ink); font-size: 12.5px; }
.uv-foot { margin: 8px 0 0; font-size: 11.5px; color: var(--mute); }
.uv-foot .ko { color: #ff8a7a; } .uv-foot .heal { color: #7fd3ff; }
</style>
